import { memo, useRef, useState } from "react";

import { useFeature } from "byzantine";
import { IconButton, LoadingShim, Popover, Tag } from "@narmi/design_system";
import { useLoadingContext, CsrfTokenMiddleware } from "cerulean";
import { useLocalization } from "@fluent/react";
import cc from "classcat";

import { useCurrentUser } from "../../../../components/contexts/CurrentUserContext";
import { maskUsername } from "../../../utils/maskUsername";

import { useGetLinkedProfiles } from "./sidebar.profile.switcher.hooks";
import styles from "./SidebarProfileSwitcher.module.scss";

import type { LinkedProfile } from "./sidebar.profile.switcher.types";

const LinkedUser = memo(({ profile }: { profile: LinkedProfile }) => {
  const { l10n } = useLocalization();
  const { currentUser } = useCurrentUser();
  const isActive = currentUser?.username === profile.username;
  const switchBetweenUsersForm = useRef<HTMLFormElement>(null);
  const unlinkForm = useRef<HTMLFormElement>(null);
  const { setIsLoading } = useLoadingContext();
  const [isUnlinkPopoverOpen, setIsUnlinkPopoverOpen] = useState(false);

  const handleSubmit = () => {
    setIsLoading(true);
    switchBetweenUsersForm.current?.submit();
  };

  const handleUnlink = () => {
    setIsLoading(true);
    unlinkForm.current?.submit();
  };

  const maskedUsername = maskUsername(profile.username);

  const ariaLabel = isActive
    ? l10n.getString("aria-label-current-profile", {
        username: maskedUsername,
      })
    : l10n.getString("aria-label-switch-profile", {
        username: maskedUsername,
      });

  return (
    <div
      className={cc([styles["sidebar-profile-switcher-popover-profile-row"]])}
    >
      <form action="/login/switch/" method="POST" ref={switchBetweenUsersForm}>
        <CsrfTokenMiddleware />
        <input type="hidden" name="username" value={profile.username} />
        <button
          disabled={isActive}
          type="button"
          className={cc([styles["sidebar-profile-switcher-popover-details"]])}
          data-testid={`link-profile-name-${profile.username}`}
          onClick={handleSubmit}
          role="menuitem"
          aria-label={ariaLabel}
          aria-current={isActive}
        >
          <div
            className={cc([
              styles["sidebar-profile-switcher-popover-details-heading"],
            ])}
          >
            <h5 className="nds-sans">{profile.full_name}</h5>
            {isActive && (
              <Tag
                kind="subdued"
                label={l10n.getString(
                  "topnav-profile-current",
                  null,
                  "Current"
                )}
              />
            )}
          </div>
          <span
            className={cc([
              styles["sidebar-profile-switcher-popover-details-subheading"],
            ])}
          >
            {profile.user_category === "personal"
              ? l10n.getString(
                  "sidebar-profile-personal-banking",
                  null,
                  "Personal banking"
                )
              : profile.public_name}
          </span>
        </button>
      </form>
      {!isActive && (
        <div
          className={cc([
            styles["sidebar-profile-switcher-popover-unlink-options"],
          ])}
        >
          <Popover
            isOpen={isUnlinkPopoverOpen}
            onUserDismiss={() => setIsUnlinkPopoverOpen(false)}
            onUserEnable={() => setIsUnlinkPopoverOpen(true)}
            content={
              <div
                className={cc([
                  styles["sidebar-profile-switcher-popover-unlink-menu"],
                ])}
              >
                <form action="/login/unlink/" method="POST" ref={unlinkForm}>
                  <CsrfTokenMiddleware />
                  <input
                    type="hidden"
                    name="username"
                    value={profile.username}
                  />
                  <button
                    type="button"
                    onClick={handleUnlink}
                    className={cc([
                      styles["sidebar-profile-switcher-popover-unlink-button"],
                    ])}
                  >
                    <span
                      className="narmi-icon-trash-2"
                      style={{
                        color: "#b42318",
                        fontSize: "1rem",
                        justifyContent: "center",
                        display: "inline-flex",
                      }}
                    />
                    {l10n.getString("button-unlink-profile", null, "Unlink")}
                  </button>
                </form>
              </div>
            }
          >
            <div
              className={
                styles["sidebar-profile-switcher-popover-kebab-button"]
              }
            >
              <IconButton
                name="more-vertical"
                kind="plain"
                textSize="l"
                label={l10n.getString(
                  "aria-label-profile-options",
                  null,
                  "Profile options"
                )}
              />
            </div>
          </Popover>
        </div>
      )}
    </div>
  );
});
LinkedUser.displayName = "LinkedUser";

const LinkedProfileList = ({
  deviceRemembered,
}: {
  deviceRemembered: boolean;
}) => {
  const { featureEnabled } = useFeature();
  const isProfileSwitcherFeatureEnabled = featureEnabled("profile_switcher");
  // If we have a remembered account & enable profile switching, allow linking another account
  const isProfileSwitcherEnabled =
    isProfileSwitcherFeatureEnabled && deviceRemembered;
  const { isFetching, linkedProfiles } = useGetLinkedProfiles(
    isProfileSwitcherEnabled
  );

  return (
    <LoadingShim isLoading={isFetching}>
      <div
        className={cc({ "margin--y--s": !!linkedProfiles.length })}
        style={{
          minHeight: isFetching ? "100px" : "0px",
          transition: "min-height 0.3s ease",
        }}
      >
        {linkedProfiles.map((profile) => (
          <LinkedUser profile={profile} key={profile.username} />
        ))}
      </div>
    </LoadingShim>
  );
};

export default memo(LinkedProfileList);
